<script setup>
import { ref, reactive, provide, watch, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useMainStore } from '@/stores/main'
import * as Auth from '@/services/Auth'
import * as Server from '@/services/Server'
import { buildQueryString, setPageTitle } from '@/services/utils'
import Loading from '@/components/Loading.vue'
import NotFound from '@/components/NotFound.vue'
import TournamentPageHeader from './Tournament/TournamentPageHeader.vue'
import TournamentMenu from './Tournament/Menu.vue'
import AddRounds from './TournamentForm/AddRounds.vue'
import RoundsList from './Tournament/RoundsList.vue'

const route = useRoute()
const store = useMainStore()

const loading = ref(true)
const tournament = ref(null)
const data = ref(null)

// editMode default true if user is authenticated, to allow quick editing. Can be toggled in header.
const editMode = ref(Auth.isAuthenticated())
const allEntries = ref({})
const allTeams = ref({})
const allSeasons = ref([])
const seasonLevels = ref([])
const levelStages = ref([])

const currents = ref({
  season: null,
  level: null,
  stage: null,
  group: null,
  round: null,
  game: null,
})

const fetchTournament = () => {
  loading.value = true
  
  const path = store.apiPath.tournaments + '/' + route.params.slug
  const queryString = buildQueryString(route.query, ['s', 'l', 'st', 'g'])

  Server.get(path + queryString)
    .then(response => {
      data.value = response.data
      tournament.value = response.data.tournament
      allSeasons.value = response.data.allSeasons
      seasonLevels.value = response.data.seasonLevels
      levelStages.value = response.data.levelStages
      allEntries.value = response.data.allEntries
      allTeams.value = response.data.allTeams || {}

      if (!tournament.value.settings){
        tournament.value.settings = {}
      }

      setCurrents()
    })
    .catch(error => {
      console.error('Error fetching tournament:', error)
      tournament.value = null
    })
    .finally(() => {
      loading.value = false
    })
}

onMounted(fetchTournament)

watch(
  () => route.params.slug,
  (slug, oldSlug) => {
    if (slug !== oldSlug) {
      fetchTournament()
    }
  }
)

watch(
  () => route.query,
  () => fetchTournament(),
  { deep: true }
)

const setCurrents = () => {
  currents.value.season = tournament.value.seasons[0]
  currents.value.level = currents.value.season.levels[0] ?? null
  currents.value.stage = currents.value.level?.stages[0] ?? null

  if (currents.value.stage?.groups && currents.value.stage.groups.length == 1){
    currents.value.group = currents.value.stage.groups[0]
  }
}

const filterNotFound = computed(() => {
  if (!tournament.value) return null
  const season = currents.value.season
  const level = currents.value.level
  const stage = currents.value.stage

  if (route.query.s && !season) return { type: 'season', value: route.query.s }
  if (route.query.l && season && !level) return { type: 'level', value: route.query.l }
  if (route.query.st && level && !stage) return { type: 'stage', value: route.query.st }
  if (route.query.g && stage && !stage.groups?.length) return { type: 'group', value: route.query.g }
  return null
})

provide('tournament', tournament)
provide('editMode', editMode)
provide('allEntries', allEntries)
provide('allTeams', allTeams)
provide('currents', currents)
provide('allSeasons', allSeasons)
provide('seasonLevels', seasonLevels)
provide('levelStages', levelStages)
</script>

<template>  
    <Loading v-if="loading" />
    <NotFound v-else-if="!tournament" />

    <template v-else>
      <v-container fluid>
        <v-row density="no-gutters">
          <v-col cols="12" md="9">
            <TournamentPageHeader />
            <v-alert v-if="filterNotFound" type="warning" variant="tonal" class="ma-4">
              {{ $t(`filterNotFound.${filterNotFound.type}`, { value: filterNotFound.value }) }}
            </v-alert>
            <router-view v-else />
          </v-col>

          <v-col cols="12" md="3">
            <TournamentMenu />
            <RoundsList />

            <div v-if="editMode" class="d-flex justify-center mt-8">
              <AddRounds
                v-model="currents.group"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
      
    </template>
  
</template>